import React, { useEffect, useRef, useState } from 'react';
import {
  ScrollView,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableHighlight,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import Checkbox from 'expo-checkbox';
import { ActivityIndicator } from 'react-native-paper';
import { FontAwesome5 } from '@expo/vector-icons';

import {
  getProductCategoriesData,
  getProductFamiliesData,
  getProductLinesData,
} from '../../../../api/Product';
import { getLocationsData } from '../../../../api/Settings';
import {
  createReconciliation,
  getReconciliationsList,
  getReconciliationDetail,
  scanReconciliationItem,
  setReconciliationItemAction,
  processReconciliation,
} from '../../../../api/Reconciliation';
import { msgStr } from '../../../../common/constants/Message';
import { useAlertModal, useConfirmModal } from '../../../../common/hooks';
import BasicLayout from '../../../../common/components/CustomLayout/BasicLayout';
import { formatDate, formatDateTime } from '../../../../common/utils/DateUtils';
import { productsStyle as styles } from '../products/styles/ProductsStyle';

const StatusObj = {
  0: 'Ready',
  1: 'Ordered',
  2: 'Checked out',
  3: 'Checked in',
  4: 'Broken',
  5: 'Sold',
  6: 'Transferred',
  7: 'Stolen',
  8: 'Trashed',
};

const getLocationLabel = (loc?: { name?: string; location?: string }) => {
  if (!loc) return '';
  return `${loc?.name ? loc.name + ' - ' : ''}${loc?.location ?? ''}`;
};

const Reconciliation = ({ navigation, openInventory }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  // 'list' -> browse past sessions & start a new one
  // 'detail' -> active session: scan + report + process
  const [view, setView] = useState<'list' | 'detail'>('list');

  const [sessions, setSessions] = useState<any[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);

  // New session form
  const [categories, setCategories] = useState<any[]>([]);
  const [families, setFamilies] = useState<any[]>([]);
  const [lines, setLines] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  const [newDate, setNewDate] = useState(formatDate(new Date()));
  const [newLocationId, setNewLocationId] = useState(0);
  const [newCategoryId, setNewCategoryId] = useState(0);
  const [newFamilyId, setNewFamilyId] = useState(0);
  const [newLineId, setNewLineId] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  // Active session detail
  const [activeId, setActiveId] = useState<number | null>(null);
  const [reconciliation, setReconciliation] = useState<any>(null);
  const [reportRows, setReportRows] = useState<any[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [scanBarcode, setScanBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const scanInputRef = useRef<any>(null);

  const loadSessions = () => {
    setIsLoadingList(true);
    getReconciliationsList((jsonRes, status) => {
      if (status === 200) setSessions(jsonRes || []);
      else showAlert('error', msgStr('unknownError'));
      setIsLoadingList(false);
    });
  };

  useEffect(() => {
    if (view === 'list') loadSessions();
  }, [view]);

  useEffect(() => {
    getProductCategoriesData((res) => setCategories(res || []));
    getLocationsData({}, (res) => setLocations(res || []));
  }, []);

  useEffect(() => {
    if (newCategoryId) {
      getProductFamiliesData(newCategoryId, (res) => setFamilies(res || []));
    } else {
      setFamilies([]);
    }
    setNewFamilyId(0);
  }, [newCategoryId]);

  useEffect(() => {
    if (newFamilyId) {
      getProductLinesData(newFamilyId, (res) => setLines(res || []));
    } else {
      setLines([]);
    }
    setNewLineId(0);
  }, [newFamilyId]);

  const handleCreateSession = () => {
    if (!newLocationId) {
      showAlert('error', 'Please select a location for this reconciliation');
      return;
    }
    setIsCreating(true);
    createReconciliation(
      {
        date: newDate,
        location_id: newLocationId,
        category_id: newCategoryId || null,
        family_id: newFamilyId || null,
        line_id: newLineId || null,
      },
      (jsonRes, status) => {
        setIsCreating(false);
        if (status === 200) {
          openSession(jsonRes.id);
        } else {
          showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
        }
      }
    );
  };

  // Merge scanned + missing + already-flagged-missing items into one editable
  // report array (each row is independent local state until "Save" persists it).
  const buildReportRows = (detail: any) => {
    const scannedRows = (detail.scannedItems || []).map((item: any) => ({
      itemId: item.id,
      productId: item.product_id,
      barcode: item.product?.barcode || item.barcode,
      family: item.product?.family?.display_name || item.product?.family?.family || '',
      line: item.product?.line?.line || '',
      size: item.product?.line?.size || '',
      currentLocationLabel: getLocationLabel(item.product?.current_location_tbl),
      currentStatus: item.product?.status,
      found: true,
      scannedAt: item.scanned_at,
      new_status: item.new_status ?? '',
      new_location_id: item.new_location_id ?? 0,
      needs_repair: !!item.needs_repair,
      repair_note: item.repair_note || '',
      saved: true,
    }));

    const flaggedMissingRows = (detail.pendingActionItems || []).map((item: any) => ({
      itemId: item.id,
      productId: item.product_id,
      barcode: item.product?.barcode || item.barcode,
      family: item.product?.family?.display_name || item.product?.family?.family || '',
      line: item.product?.line?.line || '',
      size: item.product?.line?.size || '',
      currentLocationLabel: getLocationLabel(item.product?.current_location_tbl),
      currentStatus: item.product?.status,
      found: false,
      scannedAt: null,
      new_status: item.new_status ?? '',
      new_location_id: item.new_location_id ?? 0,
      needs_repair: !!item.needs_repair,
      repair_note: item.repair_note || '',
      saved: true,
    }));

    const unflaggedMissingRows = (detail.missingProducts || []).map((product: any) => ({
      itemId: null,
      productId: product.id,
      barcode: product.barcode,
      family: product.family?.display_name || product.family?.family || '',
      line: product.line?.line || '',
      size: product.line?.size || '',
      currentLocationLabel: getLocationLabel(product.current_location_tbl),
      currentStatus: product.status,
      found: false,
      scannedAt: null,
      new_status: '',
      new_location_id: 0,
      needs_repair: false,
      repair_note: '',
      saved: false,
    }));

    return [...scannedRows, ...flaggedMissingRows, ...unflaggedMissingRows];
  };

  const loadDetail = (id: number) => {
    setIsLoadingDetail(true);
    getReconciliationDetail(id, (jsonRes, status) => {
      if (status === 200) {
        setReconciliation(jsonRes.reconciliation);
        setReportRows(buildReportRows(jsonRes));
      } else {
        showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
      }
      setIsLoadingDetail(false);
    });
  };

  const openSession = (id: number) => {
    setActiveId(id);
    setView('detail');
    loadDetail(id);
    setTimeout(() => scanInputRef.current?.focus(), 150);
  };

  const backToList = () => {
    setActiveId(null);
    setReconciliation(null);
    setReportRows([]);
    setView('list');
  };

  const handleScan = () => {
    const barcode = scanBarcode.trim();
    if (!barcode || isScanning || !activeId) return;

    setIsScanning(true);
    scanReconciliationItem(activeId, { barcode }, (jsonRes, status) => {
      setIsScanning(false);
      if (status === 200) {
        setScanBarcode('');
        loadDetail(activeId);
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else if (status === 404) {
        showAlert('error', 'No product found for that barcode');
      } else if (status === 403) {
        showAlert('error', (jsonRes && jsonRes.error) || 'This reconciliation is already processed');
      } else {
        showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
      }
    });
  };

  const updateRow = (productId: number, field: string, value: any) => {
    setReportRows((prev) =>
      prev.map((row) => (row.productId === productId ? { ...row, [field]: value, saved: false } : row))
    );
  };

  const saveRow = (productId: number) => {
    const row = reportRows.find((r) => r.productId === productId);
    if (!row || !activeId) return;

    setReportRows((prev) => prev.map((r) => (r.productId === productId ? { ...r, saving: true } : r)));

    setReconciliationItemAction(
      activeId,
      {
        product_id: productId,
        barcode: row.barcode,
        new_status: row.new_status === '' ? null : row.new_status,
        new_location_id: row.new_location_id ? row.new_location_id : null,
        needs_repair: row.needs_repair,
        repair_note: row.repair_note,
      },
      (jsonRes, status) => {
        if (status === 200) {
          setReportRows((prev) =>
            prev.map((r) => (r.productId === productId ? { ...r, saved: true, saving: false, itemId: jsonRes.id } : r))
          );
        } else {
          setReportRows((prev) => prev.map((r) => (r.productId === productId ? { ...r, saving: false } : r)));
          showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
        }
      }
    );
  };

  const handleProcess = () => {
    if (!activeId) return;
    const unsavedCount = reportRows.filter(
      (r) => !r.saved && (r.new_status !== '' || r.new_location_id || r.needs_repair)
    ).length;

    const doProcess = () => {
      setIsProcessing(true);
      processReconciliation(activeId, (jsonRes, status) => {
        setIsProcessing(false);
        if (status === 200) {
          showAlert('success', `Reconciliation processed — ${jsonRes.updatedCount} item(s) updated.`);
          loadDetail(activeId);
        } else {
          showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
        }
      });
    };

    if (unsavedCount > 0) {
      showConfirm(
        `${unsavedCount} item(s) have unsaved changes and will be skipped. Process the saved items anyway?`,
        doProcess,
        () => {}
      );
    } else {
      showConfirm(
        'Process this reconciliation? This will apply all set actions and lock the session.',
        doProcess,
        () => {}
      );
    }
  };

  const isProcessed = reconciliation?.status === 1;

  const scannedCount = reportRows.filter((r) => r.found).length;
  const mismatchCount = reportRows.filter(
    (r) => r.found && reconciliation && r.currentLocationLabel !== getLocationLabel(reconciliation.location)
  ).length;
  const missingCount = reportRows.filter((r) => !r.found).length;

  if (view === 'list') {
    return (
      <BasicLayout
        navigation={navigation}
        goBack={() => openInventory(null)}
        screenName={'Reconciliation'}
        isLoading={isLoadingList}
      >
        <ScrollView>
          <View style={styles.container}>
            <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 10 }}>Start New Reconciliation</Text>
            <View style={styles.toolbar}>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Date</Text>
                <TextInput
                  style={styles.searchInput}
                  value={newDate}
                  onChangeText={setNewDate}
                  placeholder="YYYY-MM-DD"
                />
              </View>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Location</Text>
                <Picker style={[styles.searchInput]} selectedValue={newLocationId} onValueChange={setNewLocationId}>
                  <Picker.Item label={'Select location...'} value={0} />
                  {locations.map((loc: any, index) => (
                    <Picker.Item key={index} label={loc.name || loc.location} value={loc.id} />
                  ))}
                </Picker>
              </View>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Category</Text>
                <Picker style={[styles.searchInput]} selectedValue={newCategoryId} onValueChange={setNewCategoryId}>
                  <Picker.Item label={'Any'} value={0} />
                  {categories.map((cat: any, index) => (
                    <Picker.Item key={index} label={cat.category} value={cat.id} />
                  ))}
                </Picker>
              </View>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Family</Text>
                <Picker
                  style={[styles.searchInput]}
                  selectedValue={newFamilyId}
                  onValueChange={setNewFamilyId}
                  enabled={!!newCategoryId}
                >
                  <Picker.Item label={'Any'} value={0} />
                  {families.map((fam: any, index) => (
                    <Picker.Item key={index} label={fam.display_name || fam.family} value={fam.id} />
                  ))}
                </Picker>
              </View>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Line</Text>
                <Picker
                  style={[styles.searchInput]}
                  selectedValue={newLineId}
                  onValueChange={setNewLineId}
                  enabled={!!newFamilyId}
                >
                  <Picker.Item label={'Any'} value={0} />
                  {lines.map((line: any, index) => (
                    <Picker.Item key={index} label={`${line.line}${line.size ? ' (' + line.size + ')' : ''}`} value={line.id} />
                  ))}
                </Picker>
              </View>
              <TouchableHighlight style={styles.button} onPress={handleCreateSession} disabled={isCreating}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <FontAwesome5 name="barcode" size={12} color="white" style={{ marginRight: 8 }} />
                  <Text style={styles.buttonText}>{isCreating ? 'Starting...' : 'Start Reconciliation'}</Text>
                </View>
              </TouchableHighlight>
            </View>

            <Text style={{ fontSize: 18, fontWeight: '600', marginTop: 20, marginBottom: 10 }}>
              Past Reconciliations
            </Text>
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <Text style={[styles.columnHeader, { width: 110 }]}>Date</Text>
                <Text style={[styles.columnHeader, { width: 200 }]}>Location</Text>
                <Text style={[styles.columnHeader, { width: 160 }]}>Type</Text>
                <Text style={[styles.columnHeader, { width: 110 }]}>Status</Text>
                <Text style={[styles.columnHeader, { width: 150 }]}>Created By</Text>
                <Text style={[styles.columnHeader, { width: 90 }]}>Open</Text>
              </View>
              <ScrollView>
                {sessions.length === 0 && !isLoadingList && (
                  <View style={{ padding: 16 }}>
                    <Text>No reconciliations yet.</Text>
                  </View>
                )}
                {sessions.map((session: any) => {
                  const typeLabel = [session.category?.category, session.family?.display_name || session.family?.family, session.line?.line]
                    .filter(Boolean)
                    .join(' > ') || 'All';
                  return (
                    <View key={session.id} style={styles.tableRow}>
                      <View style={[styles.cell, { width: 110 }]}>
                        <Text>{session.date}</Text>
                      </View>
                      <View style={[styles.cell, { width: 200 }]}>
                        <Text>{getLocationLabel(session.location)}</Text>
                      </View>
                      <View style={[styles.cell, { width: 160 }]}>
                        <Text>{typeLabel}</Text>
                      </View>
                      <View style={[styles.cell, { width: 110 }]}>
                        <Text>{session.status === 1 ? 'Processed' : 'In Progress'}</Text>
                      </View>
                      <View style={[styles.cell, { width: 150 }]}>
                        <Text>{session.created_by_name || ''}</Text>
                      </View>
                      <View style={[styles.IconCell, { width: 90 }]}>
                        <TouchableOpacity onPress={() => openSession(session.id)}>
                          <FontAwesome5 size={16} name="folder-open" color="black" />
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        </ScrollView>
      </BasicLayout>
    );
  }

  // Detail view — active reconciliation session
  return (
    <BasicLayout navigation={navigation} goBack={backToList} screenName={'Reconciliation'} isLoading={isLoadingDetail}>
      <ScrollView>
        <View style={styles.container}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <TouchableOpacity onPress={backToList} style={{ marginRight: 16 }}>
              <Text style={{ color: '#2e96e1' }}>{'< Back to list'}</Text>
            </TouchableOpacity>
            <Text style={{ fontSize: 18, fontWeight: '600' }}>
              Reconciliation — {reconciliation?.date} @ {getLocationLabel(reconciliation?.location)}
              {isProcessed ? ' (Processed)' : ''}
            </Text>
          </View>

          {!isProcessed && (
            <View style={styles.toolbar}>
              <View style={styles.searchBox}>
                <Text style={styles.searchLabel}>Scan</Text>
                <TextInput
                  ref={scanInputRef}
                  style={[styles.searchInput, { width: 220 }]}
                  placeholder="Scan barcode..."
                  value={scanBarcode}
                  onChangeText={setScanBarcode}
                  onSubmitEditing={handleScan}
                />
              </View>
              <TouchableHighlight style={styles.button} onPress={handleScan} disabled={isScanning}>
                <Text style={styles.buttonText}>{isScanning ? 'Scanning...' : 'Scan'}</Text>
              </TouchableHighlight>
              <TouchableHighlight style={styles.bulkbutton} onPress={() => activeId && loadDetail(activeId)}>
                <Text style={styles.buttonText}>Run Report</Text>
              </TouchableHighlight>
              <TouchableHighlight style={styles.quickScanButton} onPress={handleProcess} disabled={isProcessing}>
                <Text style={styles.buttonText}>{isProcessing ? 'Processing...' : 'Process'}</Text>
              </TouchableHighlight>
            </View>
          )}

          <View style={{ flexDirection: 'row', marginBottom: 10 }}>
            <Text style={{ marginRight: 20, fontWeight: '600' }}>{`Scanned: ${scannedCount}`}</Text>
            <Text style={{ marginRight: 20, fontWeight: '600', color: mismatchCount ? '#d54545' : '#333' }}>
              {`Wrong Location: ${mismatchCount}`}
            </Text>
            <Text style={{ fontWeight: '600', color: missingCount ? '#d54545' : '#333' }}>
              {`Missing: ${missingCount}`}
            </Text>
          </View>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={[styles.columnHeader, { width: 130 }]}>Barcode</Text>
              <Text style={[styles.columnHeader, { width: 160 }]}>Family / Line</Text>
              <Text style={[styles.columnHeader, { width: 90 }]}>Size</Text>
              <Text style={[styles.columnHeader, { width: 110 }]}>Found?</Text>
              <Text style={[styles.columnHeader, { width: 180 }]}>System Location</Text>
              <Text style={[styles.columnHeader, { width: 140 }]}>New Status</Text>
              <Text style={[styles.columnHeader, { width: 180 }]}>New Location</Text>
              <Text style={[styles.columnHeader, { width: 90 }]}>Repair</Text>
              <Text style={[styles.columnHeader, { width: 160 }]}>Repair Note</Text>
              <Text style={[styles.columnHeader, { width: 90 }]}>Save</Text>
            </View>
            <ScrollView>
              {reportRows.length === 0 && (
                <View style={{ padding: 16 }}>
                  <Text>No items yet — start scanning to build this report.</Text>
                </View>
              )}
              {reportRows.map((row) => {
                const isMismatch =
                  row.found && reconciliation && row.currentLocationLabel !== getLocationLabel(reconciliation.location);
                return (
                  <View
                    key={row.productId}
                    style={[styles.tableRow, !row.found ? { backgroundColor: '#fdecea' } : isMismatch ? { backgroundColor: '#fff8e1' } : {}]}
                  >
                    <View style={[styles.cell, { width: 130 }]}>
                      <Text>{row.barcode}</Text>
                    </View>
                    <View style={[styles.cell, { width: 160 }]}>
                      <Text>{`${row.family}${row.line ? ' - ' + row.line : ''}`}</Text>
                    </View>
                    <View style={[styles.cell, { width: 90 }]}>
                      <Text>{row.size}</Text>
                    </View>
                    <View style={[styles.cell, { width: 110 }]}>
                      <Text style={{ color: row.found ? '#2e7d32' : '#d54545', fontWeight: '600' }}>
                        {row.found ? 'Scanned' : 'Missing'}
                      </Text>
                    </View>
                    <View style={[styles.cell, { width: 180 }]}>
                      <Text>{row.currentLocationLabel}</Text>
                    </View>
                    <View style={[styles.cell, { width: 140, padding: 4 }]}>
                      <Picker
                        style={{ width: '100%' }}
                        enabled={!isProcessed}
                        selectedValue={row.new_status}
                        onValueChange={(val) => updateRow(row.productId, 'new_status', val)}
                      >
                        <Picker.Item label={'No change'} value={''} />
                        {Object.keys(StatusObj).map((value) => (
                          <Picker.Item key={value} label={StatusObj[value]} value={Number(value)} />
                        ))}
                      </Picker>
                    </View>
                    <View style={[styles.cell, { width: 180, padding: 4 }]}>
                      <Picker
                        style={{ width: '100%' }}
                        enabled={!isProcessed}
                        selectedValue={row.new_location_id}
                        onValueChange={(val) => updateRow(row.productId, 'new_location_id', val)}
                      >
                        <Picker.Item label={'No change'} value={0} />
                        {locations.map((loc: any, index) => (
                          <Picker.Item key={index} label={loc.name || loc.location} value={loc.id} />
                        ))}
                      </Picker>
                    </View>
                    <View style={[styles.cell, styles.IconCell, { width: 90 }]}>
                      <Checkbox
                        value={row.needs_repair}
                        disabled={isProcessed}
                        onValueChange={(val) => updateRow(row.productId, 'needs_repair', val)}
                      />
                    </View>
                    <View style={[styles.cell, { width: 160 }]}>
                      <TextInput
                        style={styles.searchInput}
                        editable={!isProcessed}
                        placeholder="Note..."
                        value={row.repair_note}
                        onChangeText={(val) => updateRow(row.productId, 'repair_note', val)}
                      />
                    </View>
                    <View style={[styles.IconCell, { width: 90 }]}>
                      {!isProcessed && (
                        <TouchableOpacity onPress={() => saveRow(row.productId)} disabled={row.saving}>
                          <FontAwesome5
                            size={16}
                            name={row.saved ? 'check-circle' : 'save'}
                            color={row.saved ? '#2e7d32' : '#2e96e1'}
                          />
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </ScrollView>
    </BasicLayout>
  );
};

export default Reconciliation;
